// **The G2 standings read and its model** (`rounds-client` **D1**).
//
// A promise-returning source, **not** an external store. Standings have no local extrapolation
// — no countdown, no interpolation, nothing that changes between readings — so a channel would
// buy nothing and would cost a notification path. It is read when `/standings` mounts, and when
// the phase read reports a phase or round change that could have graded something.
//
// **The client computes no mean.** `mean` and `rank` are carried verbatim from the server, and
// `gradedRounds` is carried beside them so a surface can *label* the denominator without
// inventing one. `client-rounds-standings` is explicit that **G2** is a mean precisely so
// standings stay meaningful when a player misses a round, and that a client-side denominator
// would silently contradict the server's — so the one arithmetic operation this module could
// plausibly perform is the one it deliberately does not.
//
// **The parse is fail-closed, because these rows carry identity.** Colony ids are still *derived*
// downstream by array index over join-ordinal-sorted competing members, so a row this module
// silently dropped would renumber every later colony and misattribute one player's score to
// another's colony. See {@link standingsFromDto}; the derivation's own removal is **D-204**, in
// `rounds-tournament`'s lane rather than here.

import type { MemberStandingDto, RoundScoreDto, StandingsDto } from './rounds-http';

/** One member's score in one graded round. */
export interface RoundScore {
  roundNo: number;
  score: number;
}

/** One row of the board, as every standings surface reads it. */
export interface MemberStanding {
  player: number;
  displayName: string;
  joinOrdinal: number;
  /** The member kind verbatim (`host`/`competitor`/`spectator`). */
  memberKind: string;
  /**
   * Whether this member competes — a colony, a submission and a standing every round.
   *
   * `host` competes (the server's own `is_competing`), so this is emphatically not
   * `memberKind === 'competitor'`. It is also what **D4**'s colony derivation counts over.
   */
  competing: boolean;
  roundScores: readonly RoundScore[];
  /** The server's arithmetic mean over graded rounds. `null` for a spectator, and before the
   * first graded round. Never recomputed here. */
  mean: number | null;
  /** The server's tournament-wide rank over the mean. `null` for a spectator. */
  rank: number | null;
}

export interface Standings {
  /** How many rounds have been graded — the mean's denominator, stated rather than derived. */
  gradedRounds: number;
  members: readonly MemberStanding[];
}

/**
 * A standings reading.
 *
 * `noGradedRound` is its own arm rather than a `ready` board that happens to be empty, because
 * the surface must *say so* — "listing every member with a score of zero and a mean of zero" is
 * the specific outcome the requirement forbids, and a caller that could not tell the two apart
 * would have to guess.
 */
export type StandingsRead =
  | { kind: 'ready'; standings: Standings }
  | { kind: 'noGradedRound'; standings: Standings }
  | { kind: 'failed'; message: string };

export interface StandingsSource {
  /** Reads the board now. Never throws: a failure is a named value. */
  read(): Promise<StandingsRead>;
}

export interface StandingsSourceDeps {
  transport: { fetchStandings(): Promise<StandingsDto | null> };
}

/** That member's score in `roundNo`, or `null` if the round is not graded for them. */
export function scoreForRound(member: MemberStanding, roundNo: number): number | null {
  return member.roundScores.find((row) => row.roundNo === roundNo)?.score ?? null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * An **identity ordinal**: a finite, non-negative integer, or `null` when the value is not one.
 *
 * `player` and `joinOrdinal` used to be read through a `numberOr(…, 0)` default, which is the same
 * class of defect as substituting colony `0` for an absent colony id: `0` is a real player and a
 * real join ordinal (the host's), so defaulting to it does not mark the value absent — it asserts a
 * specific, wrong identity, and two defaulted rows assert the *same* one.
 */
function identityOrdinal(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

function memberFromDto(raw: unknown): MemberStanding | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const row = raw as Partial<MemberStandingDto> & Record<string, unknown>;
  if (typeof row['displayName'] !== 'string' || typeof row['memberKind'] !== 'string') {
    return null;
  }
  const player = identityOrdinal(row['player']);
  const joinOrdinal = identityOrdinal(row['joinOrdinal']);
  if (player === null || joinOrdinal === null) {
    return null;
  }
  if (!Array.isArray(row['roundScores'])) {
    return null;
  }
  const roundScores: RoundScore[] = [];
  for (const entry of row['roundScores'] as unknown[]) {
    if (entry === null || typeof entry !== 'object') {
      return null;
    }
    const score = entry as Partial<RoundScoreDto>;
    if (typeof score.roundNo !== 'number' || typeof score.score !== 'number') {
      return null;
    }
    roundScores.push({ roundNo: score.roundNo, score: score.score });
  }
  roundScores.sort((a, b) => a.roundNo - b.roundNo);
  const memberKind = row['memberKind'];
  return {
    player,
    displayName: row['displayName'],
    joinOrdinal,
    memberKind,
    competing: memberKind !== 'spectator',
    roundScores,
    mean: optionalNumber(row['mean']),
    rank: optionalNumber(row['rank']),
  };
}

/**
 * Parses `GET /rounds/tournament/standings`, or `null` when the board cannot be read **whole**.
 *
 * **Fail-closed on every row** (implementation review **F2**). This used to drop a single
 * malformed member row and return the rest as a valid board, on the reasoning that one unreadable
 * row is a row missing from a list. That reasoning is wrong here, because these rows are
 * *identity-bearing*: colony ids are derived by array index after sorting on `joinOrdinal`
 * (`ui/rounds/colony-placement.ts`), so a dropped row does not leave a hole — it shifts every later
 * member down one, and colony 2's score is then presented under colony 1's name. The own-colony
 * cross-check does not catch it: it can only observe an omission at or before the caller, so a
 * caller at colony 0 with a malformed colony-1 row passes the check while the misattribution
 * happens behind them. A named unavailable board is a worse view; a misattributed one is a lie
 * about who scored what in a competitive tournament.
 *
 * The row-level checks are therefore all-or-nothing, and identities must additionally be **unique**
 * — two rows sharing a `joinOrdinal` would make every derived colony id depend on the answer's
 * array order rather than on the server's assignment rule.
 *
 * Nothing here filters to *competing* rows before deciding, deliberately: `memberKind` is the field
 * that decides whether a row competes, so a row malformed enough to be dropped is a row whose
 * competing-ness cannot be trusted either.
 *
 * The index derivation itself is unchanged and stays a derivation: publishing `colonyId` per row
 * server-side is recorded as **D-204** and belongs to `rounds-tournament`, not here.
 */
export function standingsFromDto(json: unknown): Standings | null {
  if (json === null || typeof json !== 'object' || Array.isArray(json)) {
    return null;
  }
  const named = json as Partial<StandingsDto> & Record<string, unknown>;
  if (typeof named['gradedRounds'] !== 'number' || !Array.isArray(named['members'])) {
    return null;
  }
  const members: MemberStanding[] = [];
  const seenPlayers = new Set<number>();
  const seenOrdinals = new Set<number>();
  for (const raw of named['members'] as unknown[]) {
    const member = memberFromDto(raw);
    if (member === null) {
      return null;
    }
    if (seenPlayers.has(member.player) || seenOrdinals.has(member.joinOrdinal)) {
      return null;
    }
    seenPlayers.add(member.player);
    seenOrdinals.add(member.joinOrdinal);
    members.push(member);
  }
  return { gradedRounds: named['gradedRounds'], members };
}

export function createStandingsSource(deps: StandingsSourceDeps): StandingsSource {
  return {
    async read(): Promise<StandingsRead> {
      const dto = await deps.transport.fetchStandings();
      if (dto === null) {
        return { kind: 'failed', message: 'the standings could not be read' };
      }
      const standings = standingsFromDto(dto);
      if (standings === null) {
        return { kind: 'failed', message: 'the standings could not be read' };
      }
      return standings.gradedRounds === 0
        ? { kind: 'noGradedRound', standings }
        : { kind: 'ready', standings };
    },
  };
}
